import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import config from "../../Config";
import * as Sentry from "@sentry/react";
import { v4 as uuidv4 } from "uuid";
import {
    Box,
    Button,
    TextField,
    Typography,
    Stack,
    useTheme,
    useMediaQuery,
    Alert,
} from "@mui/material";
import styles from "./Register.styles";
import SnackbarNotification from "../../components/SnackbarNotification";

const Register = () => {
    const [login, setLogin] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");

    const [loginError, setLoginError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [confirmPasswordError, setConfirmPasswordError] = useState("");
    const [emailError, setEmailError] = useState("");
    const [nameError, setNameError] = useState("");
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });

    // State for registration success
    const [registrationSuccess, setRegistrationSuccess] = useState(false);

    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const validateForm = () => {
        let isValid = true;
        setLoginError("");
        setPasswordError("");
        setConfirmPasswordError("");
        setEmailError("");
        setNameError("");

        if (!login.trim()) {
            setLoginError("Please enter your login");
            isValid = false;
        }

        if (!password.trim()) {
            setPasswordError("Please enter a password");
            isValid = false;
        } else if (password.length < 8) {
            setPasswordError("The password must be 8 characters or longer");
            isValid = false;
        }

        if (confirmPassword !== password) {
            setConfirmPasswordError("Passwords do not match");
            isValid = false;
        }

        if (!email.trim()) {
            setEmailError("Please enter your email");
            isValid = false;
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            setEmailError("Please enter a valid email address");
            isValid = false;
        }

        if (!name.trim()) {
            setNameError("Please enter your name");
            isValid = false;
        }

        return isValid;
    };

    const onButtonClick = () => {
        if (loading) return;
        if (!validateForm()) return;

        setLoading(true);
        register(uuidv4()).finally(() => setLoading(false));
    };

    const handleContinue = () => {
        // Navigate to verify email page with email information
        navigate("/verify-email", {
            state: {
                fromRegistration: true,
                email: email
            }
        });
    };

    const register = async (front_id) => {
        try {
            const response = await fetch(`${config.apiBaseUrl}/user/register`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ front_id, login, password, email, name }),
            });

            const data = await response.json();

            if (response.ok && data.token) {
                // Store token in localStorage but don't navigate to home
                // (because email verification is needed)
                localStorage.setItem("user", JSON.stringify({
                    login,
                    token: data.token,
                    id: data.user.id,
                    user_info: data.user
                }));

                // Show registration success screen
                setRegistrationSuccess(true);

                setSnackbar({
                    open: true,
                    message: "Registration successful! Please verify your email.",
                    severity: "success"
                });
            } else {
                // Handle error cases
                Sentry.captureMessage("Error in registration", {
                    level: "error",
                    extra: { response: data },
                });

                if (response.status === 409) {
                    // Handle conflict (user already exists)
                    setSnackbar({
                        open: true,
                        message: "User with this login or email already exists",
                        severity: "error"
                    });
                } else {
                    setSnackbar({
                        open: true,
                        message: data.detail || "Registration failed",
                        severity: "error"
                    });
                }
            }
        } catch (error) {
            Sentry.captureException(error);
            setSnackbar({
                open: true,
                message: "Something went wrong during registration",
                severity: "error"
            });
        }
    };

    // Show verification screen after successful registration
    if (registrationSuccess) {
        return (
            <Box sx={styles.container(theme, isMobile)}>
                <Box sx={{
                    width: "100%",
                    maxWidth: 600,
                    mx: "auto",
                    p: 4,
                    textAlign: "center"
                }}>
                    <Typography variant="h4" gutterBottom>
                        Registration Successful
                    </Typography>

                    <Alert severity="success" sx={{ mb: 3 }}>
                        Your account has been created successfully!
                    </Alert>

                    <Alert severity="info" sx={{ mb: 3 }}>
                        We've sent a verification link to <strong>{email}</strong>.
                        Please check your inbox and click the link to verify your account.
                    </Alert>

                    <Typography variant="body1" paragraph>
                        Before you can log in, you need to verify your email address.
                        If you don't see the email, please check your spam folder.
                    </Typography>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleContinue}
                        sx={{ mt: 2 }}
                    >
                        Continue
                    </Button>
                </Box>

                <SnackbarNotification
                    open={snackbar.open}
                    message={snackbar.message}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                />
            </Box>
        );
    }

    // Normal registration form
    return (
        <Box sx={styles.container(theme, isMobile)}>
            {!isMobile && <Box sx={styles.sidePanel} />}

            <Box sx={styles.formContainer(isMobile)}>
                <Box sx={styles.formWrapper}>
                    <Box sx={styles.headerContainer}>
                        <Typography variant="h5" sx={styles.headerText}>
                            HORIZON DATA WAVE
                        </Typography>
                        <Box sx={styles.headerLine} />
                    </Box>

                    <Typography variant="h6" gutterBottom sx={styles.formTitle}>
                        register
                    </Typography>

                    <Stack spacing={2}>
                        <TextField
                            label="Email"
                            variant="outlined"
                            fullWidth
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            error={!!emailError}
                            helperText={emailError}
                            type="email"
                        />
                        <TextField
                            label="Name"
                            variant="outlined"
                            fullWidth
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            error={!!nameError}
                            helperText={nameError}
                        />
                        <TextField
                            label="Login"
                            variant="outlined"
                            fullWidth
                            value={login}
                            onChange={(e) => setLogin(e.target.value)}
                            error={!!loginError}
                            helperText={loginError}
                        />
                        <TextField
                            label="Password"
                            variant="outlined"
                            type="password"
                            fullWidth
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            error={!!passwordError}
                            helperText={passwordError}
                        />
                        <TextField
                            label="Confirm Password"
                            variant="outlined"
                            type="password"
                            fullWidth
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            error={!!confirmPasswordError}
                            helperText={confirmPasswordError}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            onClick={onButtonClick}
                            disabled={loading}
                            sx={styles.submitButton}
                        >
                            {loading ? "Signing up..." : "SIGN UP"}
                        </Button>
                    </Stack>

                    <Typography variant="body2" color="textSecondary" textAlign="center" mt={2}>
                        Already have an account? <a href="/login">Log In</a>
                    </Typography>
                </Box>
            </Box>

            {!isMobile && <Box sx={styles.sidePanel} />}

            <SnackbarNotification
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            />
        </Box>
    );
};

export default Register;